import CardCommon from '@op/components/CardCommon';
import { barIcon, lineIcon, tableIcon } from '@op/constants/chart';
import React, { useEffect, useMemo, useState } from 'react';
import { Segment, Select } from 'tea-component';
import { CommunityOptionsType, getCommunityOptions } from '../constants';
import useLanguage from '@op/hooks/useLanguage';
import CommunityTable from './CommunityTable';
import useCompareKeyMetricsColumns from '@op/components/TableCommonHook/hooks/useCompareKeyMetricsColumns';
import CommunityCharts from './CommunityCharts';
import { SortBy } from 'tea-component/lib/table/addons';
import { DBO_U } from '@op/utils/format';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';

export default function Community({
  filter,
  gameData,
  gameInfo,
}: {
  filter: any;
  gameData: any;
  gameInfo: any;
}) {
  const { formatMessage, formatOptionsMessage } = useLanguage();
  const [value, setValue] = useState<CommunityOptionsType>('total');
  const [chartType, setChartType] = useState('line');
  const [sortBy, setSortBy] = useState<SortBy<any>>({
    by: 'time',
    order: 'desc',
  });
  const [selectValue, setSelectValue] = useState({ value: '' });
  const [options, setOptions] = useState<
    Array<{ text: string; value: string }>
  >([]);
  const { columns: communityOptions } = useCompareKeyMetricsColumns({
    oneKey: 'Community',
    twoKey: value,
    gameData,
    filter,
  });

  function getOtherParams(tab: CommunityOptionsType) {
    const otherParams: any = {};
    if (tab === 'posts') {
      otherParams['review_types'] = ['posts_text', 'sub_text'];
    }
    if (tab === 'videos') {
      otherParams['review_types'] = ['posts_video', 'sub_video'];
    }

    return otherParams;
  }

  useEffect(() => {
    const list =
      communityOptions
        .filter((val) => val.key !== 'game')
        .map((val) => ({
          text: val.text,
          value: val.key,
          ...val,
        })) || [];
    setOptions(list);
    setSelectValue({ value: list?.[0]?.value || '' });
  }, [communityOptions]);

  const selectedOptions = useMemo(
    () => options.filter((val) => val.value === selectValue.value)[0],
    [selectValue, options],
  );

  return (
    <CardCommon
      className="scroll-margin-top-target"
      title={formatMessage({ id: 'Community' })}
      id="Community"
    >
      <CardCommon
        title={
          <>
            <Segment
              value={value}
              onChange={(value: CommunityOptionsType) => {
                setValue(value);
                buriedBtnPoint('CKC_TypeTAB', value);
              }}
              options={getCommunityOptions(formatMessage, value)}
              className="dbo-compare-keymetrics-community-tab"
            />
            <Select
              appearance="button"
              options={formatOptionsMessage(options)}
              value={selectValue.value}
              style={{ width: 192, fontWeight: 400 }}
              listWidth={192}
              onChange={(value: string) => {
                setSelectValue({ value });
                buriedBtnPoint('CKC_Metrics', value);
              }}
            ></Select>
          </>
        }
        hasDownload
        contentCard
        operations={[tableIcon, lineIcon, barIcon]}
        defaultOperation={chartType}
        handleOperationsOnClick={(item) => {
          setChartType(item?.symbol || 'line');
        }}
        handleDownloadOnClick={() => {
          const selectedOptions = options.filter(
            (val) => val.value === selectValue.value,
          )[0];
          const otherParams: any = getOtherParams(value);
          DBO_U.download({
            key: 'CompareKeyMetricsCommunityChart',
            fileName: 'Compare Key Metrics - Community',
            filter: {
              ...filter,
              tab_type: value,
              by_type: selectValue.value,
              stats_types: ['community'],
              ...otherParams,
            },
            auxiliaryParams: {
              metricHeader: selectedOptions?.text,
              isEngagementRate: selectValue.value === 'engagement_rate',
              filter,
              gameData,
            },
          });
        }}
        logBtnId={'Community'}
      >
        <CommunityCharts
          filter={filter}
          tab={value}
          chartType={chartType}
          byType={selectValue}
          getOtherParams={getOtherParams}
          gameData={gameData}
          chartMap={selectedOptions}
          setSortBy={setSortBy}
          sortBy={sortBy}
        ></CommunityCharts>
        <CommunityTable
          filter={filter}
          tab={value}
          getOtherParams={getOtherParams}
          gameData={gameData}
          gameInfo={gameInfo}
        ></CommunityTable>
      </CardCommon>
    </CardCommon>
  );
}
